/**
 * Changing a deal after it has been written.
 *
 * Everything here is a rule about what may be altered and when, kept out of
 * the screen so the screen only has to render it.
 */

import { supabase } from "../../lib/supabase";
import { nothingChanged } from "../../lib/saving";

/** Fields a person may change on an existing deal.
    The tax columns are generated, so they recompute themselves and are
    deliberately absent — there is no way to type a tax figure that disagrees
    with the contract. */
export interface DealEdits {
  /** Who is buying. Changeable: a deal quoted for one person and signed by
      another is common enough, and re-keying the whole deal to fix it is
      how wrong figures get typed. */
  buyer_id: string;
  /** The second name on the title, and how the two are joined. */
  co_buyer_id: string | null;
  joint_ownership: "and" | "or" | null;
  agreement_date: string;
  county_name: string;
  county_surtax_bps: number;
  cash_price_cents: number;
  predelivery_service_cents: number;
  electronic_filing_cents: number;
  license_registration_cents: number;
  title_fee_cents: number;
  temp_tag_cents: number;
  trade_allowance_cents: number;
  trade_payoff_cents: number;
  cash_down_cents: number;
  deferred_down_cents: number;
  warranty_days: number;
  warranty_miles: number;
  insurance_company: string | null;
  insurance_policy_no: string | null;
  insurance_company_code: string | null;
}

export async function saveDealEdits(id: string, edits: DealEdits) {
  const { data, error } = await supabase.from("deals").update(edits).eq("id", id).select("id");
  if (error) throw new Error(`Could not save the changes: ${error.message}`);
  if (!data?.length) throw nothingChanged();
}

/* ------------------------------------------------------------------ */
/* Payments                                                            */
/* ------------------------------------------------------------------ */

export interface PaymentEdits {
  paid_on: string;
  amount_cents: number;
  method: string;
  reference_no: string | null;
}

export async function savePaymentEdits(id: string, edits: PaymentEdits) {
  const { data, error } = await supabase.from("payments").update(edits).eq("id", id).select("id");
  if (!error && !data?.length) throw nothingChanged();
  if (error) {
    // The database refuses a future date and a non-positive amount.
    if (/future/i.test(error.message)) {
      throw new Error("That payment date is in the future.");
    }
    if (/amount_positive/i.test(error.message)) {
      throw new Error("A payment has to be more than zero. To undo one, reverse it.");
    }
    throw new Error(`Could not save the payment: ${error.message}`);
  }
}

/**
 * Reverse a payment: a bounced check, a disputed card.
 *
 * The row stays and stops counting toward the balance. Deleting it instead
 * would lose the fact that money was once recorded against this deal, which
 * is the thing anyone reviewing the file later actually wants to see.
 */
export async function reversePayment(id: string, reason: string) {
  const { data, error } = await supabase.from("payments").update({
    reversed_at: new Date().toISOString(),
    reversed_reason: reason.trim() || "No reason given",
  }).eq("id", id).select("id");
  if (error) throw new Error(`Could not reverse the payment: ${error.message}`);
  if (!data?.length) throw nothingChanged();
}

export async function unreversePayment(id: string) {
  const { data, error } = await supabase.from("payments")
    .update({ reversed_at: null, reversed_reason: null }).eq("id", id).select("id");
  if (error) throw new Error(`Could not undo the reversal: ${error.message}`);
  if (!data?.length) throw nothingChanged();
}

/* ------------------------------------------------------------------ */
/* Deleting                                                            */
/* ------------------------------------------------------------------ */

/**
 * Whether this deal may be deleted, and if not, why not.
 *
 * Only a quote can go. A quote is a proposal: nothing has been signed, no
 * money has been taken and no paperwork exists, so there is nothing to keep.
 * Everything past that is a record — void it instead, which undoes the sale
 * and returns the car to the lot while the deal stays on file.
 *
 * Status alone is not enough. A deal that was sold, had its packet filed and
 * was later moved back to quote would pass a status check while still having
 * records hanging off it, so those are counted rather than assumed.
 */
export function deleteBlockedReason(
  status: string,
  paymentCount: number,
  documentCount: number,
): string | null {
  // A quote is a proposal and a voided deal is one that was unwound: neither
  // is a sale, so neither is a record anyone has to keep. Everything between
  // them involves money taken or paperwork signed.
  if (status !== "quote" && status !== "voided") {
    return `Only a quote or a voided deal can be deleted. This one is ${status} `
         + "— void it first, which returns the car to the lot and keeps the "
         + "deal on file.";
  }
  if (documentCount > 0) {
    return `There ${documentCount === 1 ? "is" : "are"} ${documentCount} filed `
         + `document${documentCount === 1 ? "" : "s"} against this deal, so it is a `
         + "record and has to stay.";
  }
  if (paymentCount > 0) {
    return `There ${paymentCount === 1 ? "is" : "are"} ${paymentCount} payment`
         + `${paymentCount === 1 ? "" : "s"} recorded against this deal. Reverse or `
         + "remove the money first.";
  }
  return null;
}

export async function deleteDeal(id: string) {
  const { data, error } = await supabase.from("deals").delete().eq("id", id).select("id");
  if (!error && !data?.length) throw nothingChanged();
  if (error) {
    if (/foreign key|violates/i.test(error.message)) {
      throw new Error(
        "Something is still filed against this deal, so it cannot be deleted. "
        + "Void it instead.",
      );
    }
    throw new Error(`Could not delete: ${error.message}`);
  }
}
